import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { WhiteLabelSettingsModel } from '@atlas-workspace/shared/models';
import CssFilterConverter from 'css-filter-converter';
import { ColorToFilterResult } from 'css-filter-converter/lib/shared/types/result';

@Injectable()
export class ClientWhiteLabelService {
  private readonly sheet!: CSSStyleSheet;
  private readonly body!: HTMLElement;
  private readonly bodyClass = 'white-label';

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.sheet = new CSSStyleSheet();
    this.body = this.document.body;
  }

  generateStyleSheet(data: WhiteLabelSettingsModel): void {
    this.sheet.replaceSync(`
      html:has(body.${this.bodyClass}) {
        --primary-text-color: #${data.primaryTextColor};
        --icon-primary-color: ${this.getFilterColor(data.primaryTextColor)};
        --button-icon-color-black-default: ${this.getFilterColor(data.primaryTextColor)};
        --button-icon-color-gray-default: ${this.getFilterColor(data.primaryTextColor)};

        --secondary-text-color: #${data.secondaryTextColor};
        --icon-secondary-color: ${this.getFilterColor(data.secondaryTextColor)};

        --placeholder-text-color: #${data.placeholderTextColor};
        --disabled-text-color: #${data.disabledTextColor};

        --accent-color: #${data.accentColor};
        --accent-color-blue-default: #${data.accentColor};
        --accent-color-blue-default-925: #${data.accentColor};
        --icon-accent-color: ${this.getFilterColor(data.accentColor)};
        --icon-accent-color-blue-default: ${this.getFilterColor(data.accentColor)};

        --checkbox-color: #${data.checkboxColor};
        --checkbox-hover-color: #${data.checkboxHoverColor};

        --tab-selected-state: #${data.tabSelectedState};
        --tab-hovered-state: #${data.tabHoveredState};

        --sidebar-tab-hovered-state: #${data.sidebarTabHoveredState};
        --sidebar-tab-selected-state: #${data.sidebarTabSelectedState};
        --grouped-drop-down-hovered-state: #${data.sidebarTabHoveredState};
        --grouped-drop-down-selected-state: #${data.sidebarTabSelectedState};
        --header-color: #${data.headerColor};
        --options_header_color: #${data.optionsHeaderColor};
        --background-layout: #${data.backgroundColor};

        --border-color: #${data.borderColor};
        --border-hover-color: #${data.borderHoverColor};
        --border-color-transparent: #${data.borderColor};

        --button-border-radius: ${data.buttonCornerRadius}px;
        --button-border-radius-default-16: ${data.buttonCornerRadius}px;
        --button-border-radius-default-40: ${data.buttonCornerRadius}px;

        --primary-button-background-disabled: #${data.primaryButtonBackgroundDisabled};
        --primary-button-background-hover: #${data.primaryButtonBackgroundHover};
        --primary-button-background-rest: #${data.primaryButtonBackgroundRest};

        --primary-button-border-disabled: #${data.primaryButtonBorderDisabled};
        --primary-button-border-hover: #${data.primaryButtonBorderHover};
        --primary-button-border-rest: #${data.primaryButtonBorderRest};

        --primary-button-text-color-rest: #${data.primaryButtonTextColorRest};
        --primary-button-icon-color-rest: ${this.getFilterColor(data.primaryButtonTextColorRest)};
        --primary-button-text-color-hover: #${data.primaryButtonTextColorHover};
        --primary-button-icon-color-hover: ${this.getFilterColor(data.primaryButtonTextColorHover)};
        --primary-button-text-color-disabled: #${data.primaryButtonTextColorDisabled};
        --primary-button-icon-color-disabled: ${this.getFilterColor(data.primaryButtonTextColorDisabled)};

        --secondary-button-background-disabled: #${data.secondaryButtonBackgroundDisabled};
        --secondary-button-background-hover: #${data.secondaryButtonBackgroundHover};
        --secondary-button-background-rest: #${data.secondaryButtonBackgroundRest};

        --secondary-button-border-disabled: #${data.secondaryButtonBorderDisabled};
        --secondary-button-border-hover: #${data.secondaryButtonBorderHover};
        --secondary-button-border-rest: #${data.secondaryButtonBorderRest};

        --secondary-button-text-color-rest: #${data.secondaryButtonTextColorRest};
        --secondary-button-icon-color-rest: ${this.getFilterColor(data.secondaryButtonTextColorRest)};
        --secondary-button-text-color-hover: #${data.secondaryButtonTextColorHover};
        --secondary-button-icon-color-hover: ${this.getFilterColor(data.secondaryButtonTextColorHover)};
        --secondary-button-text-color-disabled: #${data.secondaryButtonTextColorDisabled};
        --secondary-button-icon-color-disabled: ${this.getFilterColor(data.secondaryButtonTextColorDisabled)};
        --secondary-button-border-radius: ${data.secondaryButtonCornerRadius}px;
      }
  `);
    this.document.adoptedStyleSheets = [this.sheet];
    this.bodyWhiteLabelAddClass();
  }

  bodyWhiteLabelAddClass(): void {
    if (!this.body.classList.contains(this.bodyClass)) {
      this.body.classList.add(this.bodyClass);
    }
  }

  bodyWhiteLabelRemoveClass(): void {
    if (this.body.classList.contains(this.bodyClass)) {
      this.body.classList.remove(this.bodyClass);
    }
  }

  cssFilterConverter(hex: string): ColorToFilterResult<string | null> {
    return CssFilterConverter.hexToFilter(hex);
  }

  private getFilterColor(hex: string): string {
    return this.cssFilterConverter(`#${hex}`).color || 'none';
  }
}
